/** Observe the stylesheet itself, not just a transient preload load event.
 * Embedded browsers can finish preloading before the application subscribes.
 * Activating the same link is idempotent and reuses its already downloaded bytes.
 */
export function waitForPageStyles(): Promise<void[]> {
  return Promise.all(
    Array.from(document.querySelectorAll<HTMLLinkElement>('link[data-app-style]')).map(
      (link) => new Promise<void>((resolve, reject) => {
        let poll: ReturnType<typeof setTimeout> | undefined
        let settled = false
        const cleanup = () => {
          settled = true
          clearTimeout(poll)
          link.removeEventListener('load', check)
          link.removeEventListener('error', failed)
        }
        const check = () => {
          if (settled) return
          if (link.sheet) {
            cleanup()
            resolve()
          }
        }
        const failed = () => {
          cleanup()
          reject(new Error('Style load failed'))
        }
        const observe = () => {
          check()
          // There is no deadline: a slow but successful transfer can still finish.
          if (!settled) poll = setTimeout(observe, 100)
        }
        link.addEventListener('load', check)
        link.addEventListener('error', failed)
        if (link.dataset.styleState === 'error') {
          failed()
          return
        }
        // Do not depend on the inline preload handler having fired successfully.
        link.rel = 'stylesheet'
        observe()
      }),
    ),
  )
}
